import React, { Component, createRef } from 'react';
import { Link } from 'react-router-dom';

// CSS
import '../Subscriptions/Subscription.scss';

// Helper
import { checkCookie } from '../../Helper/Cookies';
import { apiFetch, uploadBrandingImage, platformInfo } from '../../Helper/Api';

// Components
import Header from '../../components/Header/Header';
import { Alert } from '../../components/UI/Feedback';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Toast } from 'primereact/toast';
import { ThemeBar, StateChip, themeOf, shows, siteOf } from './SiteEditor';

// Properties
const properties = require('../../properties.json');

const EMPTY_SECTION = { icon: '', title: '', sub: '', text: '', photo: '' };

// "Site identity": the look & feel and landing copy of the teacher's public site. Every field is
// optional -- an empty one means "use the neutral default" (or hide that section) on the public
// pages. Saved through PUT /teachers/me/branding; the About page is stored as one JSON string.
// The page opens on the teacher's theme (SiteEditor.js) and marks each section with whether that theme shows it.
class Branding extends Component {
    constructor(props) {
        super(props);
        this.toast = createRef(null);
    }

    state = {
        siteTitle: '', tagline: '',
        heroEyebrow: '', heroTitle: '', heroText: '', heroImageUrl: '',
        featureTitle: '', featureText: '', featureImageUrl: '',
        downloadUrl: '', faviconUrl: '',
        aboutTitle: '', aboutRole: '', aboutPhoto: '', aboutSections: [],
        approachTitle: '', approachText: '', approachTag: '',
        saving: false,
        uploading: null,
        loading: true,
        branding: null,
        site: null,
        savedSnap: null,
    }

    componentDidMount() {
        document.title = properties.site_name + " | هوية الموقع";
        if (!checkCookie('name')) {
            window.localStorage.removeItem('currentTeacher');
            window.location.href = `/`;
            return;
        }
        apiFetch("/teachers/me")
            .then(me => {
                const b = me.branding || {};
                let about = {};
                try { about = b.aboutJson ? JSON.parse(b.aboutJson) : {}; } catch (e) { about = {}; }
                const approach = about.approach || {};
                this.setState({
                    siteTitle: b.siteTitle || '', tagline: b.tagline || '',
                    heroEyebrow: b.heroEyebrow || '', heroTitle: b.heroTitle || '', heroText: b.heroText || '', heroImageUrl: b.heroImageUrl || '',
                    featureTitle: b.featureTitle || '', featureText: b.featureText || '', featureImageUrl: b.featureImageUrl || '',
                    downloadUrl: b.downloadUrl || '', faviconUrl: b.faviconUrl || '',
                    aboutTitle: about.title || '', aboutRole: about.role || '', aboutPhoto: about.photo || '',
                    aboutSections: Array.isArray(about.sections) ? about.sections.map(s => ({ ...EMPTY_SECTION, ...s })) : [],
                    approachTitle: approach.title || '', approachText: approach.text || '', approachTag: approach.tag || '',
                    loading: false,
                    branding: b,
                    teacherName: me.name || '',
                }, () => this.setState({ savedSnap: this.snapshot() }));
                platformInfo().then(info => this.setState({ site: siteOf(me, info) })).catch(() => this.setState({ site: siteOf(me, null) }));
            })
            .catch(error => {
                this.setState({ loading: false });
                console.error('Error:', error);
            });
    }

    buildAboutJson = () => {
        const { aboutTitle, aboutRole, aboutPhoto, aboutSections, approachTitle, approachText, approachTag } = this.state;
        const clean = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== '' && v != null));
        const sections = aboutSections.map(clean).filter(s => Object.keys(s).length > 0);
        const approach = clean({ title: approachTitle, text: approachText, tag: approachTag });
        const about = clean({ title: aboutTitle, role: aboutRole, photo: aboutPhoto });
        if (sections.length) about.sections = sections;
        if (Object.keys(approach).length) about.approach = approach;
        return Object.keys(about).length ? JSON.stringify(about) : '';
    }

    /** What a save would send, as text: the page has unsaved changes while it differs from the last saved one. */
    snapshot = () => {
        const s = this.state;
        return JSON.stringify([s.siteTitle, s.tagline, s.heroEyebrow, s.heroTitle, s.heroText, s.heroImageUrl, s.featureTitle,
            s.featureText, s.featureImageUrl, s.downloadUrl, s.faviconUrl, this.buildAboutJson()]);
    }

    save = () => {
        const s = this.state;
        // colours / theme / fonts are saved from the "site look" page (SiteLook.js); the server keeps them as they are
        const body = {
            siteTitle: s.siteTitle, tagline: s.tagline,
            heroEyebrow: s.heroEyebrow, heroTitle: s.heroTitle, heroText: s.heroText, heroImageUrl: s.heroImageUrl,
            featureTitle: s.featureTitle, featureText: s.featureText, featureImageUrl: s.featureImageUrl,
            downloadUrl: s.downloadUrl, faviconUrl: s.faviconUrl,
            aboutJson: this.buildAboutJson(),
        };
        this.setState({ saving: true });
        apiFetch("/teachers/me/branding", { method: "PUT", body })
            .then(() => {
                this.setState({ saving: false, savedSnap: this.snapshot() });
                this.toast.current.show({ severity: 'success', summary: 'تم بنجاح', detail: 'تم حفظ هوية الموقع' });
            })
            .catch(error => {
                this.setState({ saving: false });
                this.toast.current.show({ severity: 'error', summary: 'لم تتم العملية', detail: error.message || 'خطأ في الإتصال' });
            });
    }

    // ---- field helpers: every field is a named box, two to a line ----

    text = (key, label, placeholder, wide = false) => (
        <label className={wide ? "se-f is-wide" : "se-f"} key={key}>
            <span>{label}</span>
            <InputText
                id={key}
                value={this.state[key]}
                placeholder={placeholder}
                onChange={(e) => this.setState({ [key]: e.target.value })}
            />
        </label>
    );

    area = (key, label, rows = 3) => (
        <label className="se-f is-wide" key={key}>
            <span>{label}</span>
            <InputTextarea
                id={key}
                rows={rows}
                autoResize
                value={this.state[key]}
                onChange={(e) => this.setState({ [key]: e.target.value })}
            />
        </label>
    );

    // An image is uploaded to storage (PNG/JPG/GIF/WEBP/ICO, up to 5 MB); the resulting URL is what gets saved.
    pickImage = (apply, uploadingKey) => (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file) return;
        this.setState({ uploading: uploadingKey });
        uploadBrandingImage(file)
            .then(url => { apply(url); this.setState({ uploading: null }); })
            .catch(error => {
                this.setState({ uploading: null });
                this.toast.current.show({ severity: 'error', summary: 'لم يتم رفع الصورة', detail: error.message || 'خطأ في الإتصال' });
            });
    };

    imageBox = (url, label, uploadingKey, onUrl, note) => {
        const busy = this.state.uploading === uploadingKey;
        return (
            <div className={`se-img ${busy ? 'is-busy' : ''}`} key={uploadingKey}>
                <span className="se-img-th">
                    {url ? <img src={url} alt="" /> : <i className="pi pi-image"></i>}
                </span>
                <span className="se-img-tx">
                    <b>{label}</b>
                    <small>{busy ? 'جاري الرفع…' : note || (url ? 'دي الصورة اللي على موقعك' : 'مفيش صورة — PNG أو JPG أو WEBP لحد 5 ميجا')}</small>
                    <span className="se-img-act">
                        <label>
                            <i className={busy ? 'pi pi-spin pi-spinner' : 'pi pi-upload'}></i>
                            {url ? 'تغيير' : 'رفع صورة'}
                            <input type="file" accept="image/png,image/jpeg,image/gif,image/webp,image/x-icon,.ico" style={{ display: 'none' }}
                                disabled={!!this.state.uploading} onChange={this.pickImage(onUrl, uploadingKey)} />
                        </label>
                        {url && <button type="button" className="is-del" onClick={() => onUrl('')}><i className="pi pi-times"></i>إزالة</button>}
                    </span>
                </span>
            </div>
        );
    };

    setSection = (i, field, value) => {
        const aboutSections = this.state.aboutSections.map((s, idx) => idx === i ? { ...s, [field]: value } : s);
        this.setState({ aboutSections });
    };

    addSection = () => this.setState({ aboutSections: [...this.state.aboutSections, { ...EMPTY_SECTION }] });

    removeSection = (i) => this.setState({ aboutSections: this.state.aboutSections.filter((_, idx) => idx !== i) });

    // ---- the theme's first screen, drawn from what is typed ----
    heroSample = (theme) => {
        const s = this.state;
        const c = theme.colors;
        const bento = theme.layout.hero === 'bento';
        return (
            <div className="se-hero" style={{ '--sp': c.primary, '--sa': c.accent, '--sb': c.bg, '--sf': `'${theme.fonts.head}'` }} aria-hidden="true">
                <span className="se-hero-tag">معاينة حيّة</span>
                <div className="cp">
                    {s.heroEyebrow && <span className="eb">{s.heroEyebrow}</span>}
                    <h4>{s.heroTitle || `اتعلّم أونلاين مع ${s.teacherName || 'المدرس'}`}</h4>
                    <p>{s.heroText || 'كورسات ودروس ومصادر في مكان واحد. اتعلّم خطوة بخطوة وعلى راحتك.'}</p>
                    <div className="bt"><i></i><i></i></div>
                </div>
                <div className="vs" style={s.heroImageUrl && !bento ? { backgroundImage: `url(${s.heroImageUrl})` } : undefined}>
                    {!(s.heroImageUrl && !bento) && (
                        <span><i className={bento ? 'pi pi-th-large' : 'pi pi-palette'}></i>{bento ? 'صناديق الثيم' : theme.home ? 'رسمة الثيم' : 'شكل الثيم'}</span>
                    )}
                    {s.tagline && s.heroImageUrl && !bento && <span className="lb">{s.tagline}</span>}
                </div>
            </div>
        );
    };

    render() {
        const { saving, loading, aboutSections, branding, site, savedSnap } = this.state;
        const theme = branding ? themeOf(branding) : null;
        const dirty = savedSnap !== null && this.snapshot() !== savedSnap;
        // the "feature" section belongs to the classic home page, and to a newer theme only if it lists that block
        const featureShown = theme ? (!theme.home || shows(theme, 'featureGrid')) : true;
        const featureFilled = !!(this.state.featureTitle || this.state.featureText);
        const bento = theme && theme.layout.hero === 'bento';
        const states = {
            'br-id': 'on',
            'br-hero': 'on',
            'br-feature': !featureShown ? 'off' : featureFilled ? 'on' : 'hidden',
            'br-app': this.state.downloadUrl ? 'on' : 'hidden',
            'br-about': 'on',
            'br-approach': (this.state.approachTitle || this.state.approachText || this.state.approachTag) ? 'on' : 'hidden',
        };
        const head = (id, n, title, sub) => (
            <h3>
                <span className="n">{n}</span>
                <span className="t">{title}<small>{sub}</small></span>
                <span className="act"><StateChip state={states[id]} /></span>
            </h3>
        );
        const why = (icon, children) => <div className="se-why"><i className={`pi ${icon}`}></i><span>{children}</span></div>;

        return (
            <>
                {/* the page's one action lives on its title strip, in sight
                    however far down the form you are; it wakes when something changed */}
                <Header
                    pageName="هوية الموقع"
                    action={{
                        label: saving ? 'جاري الحفظ…' : 'حفظ هوية الموقع',
                        icon: saving ? 'pi pi-spin pi-spinner' : 'pi pi-check',
                        disabled: saving || !dirty,
                        onClick: this.save,
                    }}
                />
                <Toast ref={this.toast}></Toast>
                <div className="content">
                    <div className="container se-page">
                        {loading ? (
                            <div className="card">
                                <Card>
                                    <div className="ui-form-wait" role="status">
                                        <span className="spin" aria-hidden="true"></span>
                                        <b>جاري تحميل هوية الموقع…</b>
                                    </div>
                                </Card>
                            </div>
                        ) : (
                            <>
                                <ThemeBar theme={theme} site={site} here="branding" editable={['hero', 'heroCta', 'featureGrid']} />

                                <div className="se-grid">
                                    <div className="se-main">
                                        <Alert kind="info" title="كل حاجة هنا اختيارية">
                                            أي حقل تسيبه فاضي هياخد الشكل الافتراضي للثيم. الألوان والخطوط والثيم نفسه من <Link to="/settings/look">شكل الموقع</Link>،
                                            ونصوص أقسام الصفحة الرئيسية (الأرقام، الآراء، الأسئلة…) من <Link to="/settings/home-content">محتوى الصفحة الرئيسية</Link>.
                                        </Alert>

                                        <section className="ui-sec" id="br-id" style={{ '--d': '0ms' }}>
                                            {head('br-id', '١', 'العنوان والأيقونة', 'اسم موقعك وأيقونته في تبويب المتصفح')}
                                            <div className="ui-fields">
                                                {this.text('siteTitle', 'عنوان الموقع (تبويب المتصفح)', '')}
                                                {this.text('tagline', 'جملة قصيرة تعريفية', '')}
                                                {this.imageBox(this.state.faviconUrl, 'أيقونة الموقع (favicon)', 'favicon', (v) => this.setState({ faviconUrl: v }), 'مربعة، وبتظهر جنب اسم موقعك في المتصفح')}
                                            </div>
                                        </section>

                                        <section className="ui-sec" id="br-hero" style={{ '--d': '60ms' }}>
                                            {head('br-hero', '٢', 'أول الشاشة', 'أول حاجة الزائر بيشوفها لما يفتح موقعك')}
                                            {theme && why('pi-eye', theme.home
                                                ? (bento
                                                    ? `ثيم «${theme.name}» بيكتب الكلام ده في الصندوق الكبير، ومش بيعرض صورة واجهة.`
                                                    : `في ثيم «${theme.name}» الصورة بتظهر مكان رسمة الثيم، والجملة التعريفية بتتكتب عليها. من غير صورة بتفضل رسمة الثيم.`)
                                                : `ثيم «${theme.name}» بيعرض الكلام ده جنب صورة الواجهة.`)}
                                            {theme && this.heroSample(theme)}
                                            <div className="ui-fields">
                                                {this.text('heroEyebrow', 'سطر صغير فوق العنوان', '')}
                                                {this.text('heroTitle', 'العنوان الرئيسي', '')}
                                                {this.area('heroText', 'النص التعريفي')}
                                                {this.imageBox(this.state.heroImageUrl, 'صورة الواجهة', 'hero', (v) => this.setState({ heroImageUrl: v }),
                                                    bento ? 'ثيمك الحالي مش بيعرضها' : null)}
                                            </div>
                                        </section>

                                        <section className={`ui-sec ${states['br-feature'] === 'off' ? 'is-off' : ''}`} id="br-feature" style={{ '--d': '120ms' }}>
                                            {head('br-feature', '٣', 'قسم «الميزة»', 'عنوان وصورة ونص عن أهم حاجة بتميّزك')}
                                            {theme && why(featureShown ? 'pi-eye' : 'pi-eye-slash', featureShown
                                                ? 'بيظهر في صفحتك الرئيسية لو كتبت فيه عنوان أو نص.'
                                                : <>ثيم «{theme.name}» مش بيعرض القسم ده — مميزاتك بتتكتب في <Link to="/settings/home-content#sc-features">محتوى الصفحة الرئيسية</Link> لو ثيمك فيه قسم مميزات.</>)}
                                            <div className="ui-fields">
                                                {this.text('featureTitle', 'عنوان القسم', '')}
                                                {this.imageBox(this.state.featureImageUrl, 'صورة القسم', 'feature', (v) => this.setState({ featureImageUrl: v }))}
                                                {this.area('featureText', 'نص القسم')}
                                            </div>
                                        </section>

                                        <section className="ui-sec" id="br-app" style={{ '--d': '180ms' }}>
                                            {head('br-app', '٤', 'تطبيق سطح المكتب', 'زرار «حمّل التطبيق» في أعلى موقعك')}
                                            {why('pi-info-circle', 'الزرار بيظهر في الهيدر بس لما يكون فيه رابط، في كل الثيمات.')}
                                            <div className="ui-fields">
                                                {this.text('downloadUrl', 'رابط التحميل', 'https://.../setup.exe', true)}
                                            </div>
                                        </section>

                                        <section className="ui-sec" id="br-about" style={{ '--d': '240ms' }}>
                                            {head('br-about', '٥', 'صفحة «عني»', 'بتظهر على الموقع لو كتبت فيها أي حاجة، أو لو عندك وصف في ملفك')}
                                            <div className="ui-fields">
                                                {this.text('aboutTitle', 'العنوان (الافتراضي: اسمك)', '')}
                                                {this.text('aboutRole', 'اللقب أو التخصص', '')}
                                                {this.imageBox(this.state.aboutPhoto, 'صورتك', 'about', (v) => this.setState({ aboutPhoto: v }))}
                                            </div>

                                            <h4 className="ui-sub">
                                                أقسام الصفحة
                                                <small>
                                                    {aboutSections.length
                                                        ? `${aboutSections.length} قسم هيظهروا بالترتيب ده`
                                                        : 'لسه مفيش أقسام — ضيف واحد وابدأ تكتب'}
                                                </small>
                                            </h4>

                                            <div className="se-rows">
                                                {aboutSections.map((s, i) => (
                                                    <div className="se-row" key={i}>
                                                        <span className="i">{i + 1}</span>
                                                        <div className="se-row-fields">
                                                            {[['icon', 'أيقونة (إيموجي)'], ['title', 'العنوان'], ['sub', 'عنوان فرعي']].map(([f, l]) => (
                                                                <label className="se-f" key={f}>
                                                                    <span>{l}</span>
                                                                    <InputText value={s[f]} onChange={(e) => this.setSection(i, f, e.target.value)} />
                                                                </label>
                                                            ))}
                                                            {this.imageBox(s.photo, 'صورة القسم', `section-${i}`, (v) => this.setSection(i, 'photo', v))}
                                                            <label className="se-f is-wide">
                                                                <span>النص</span>
                                                                <InputTextarea rows={3} autoResize value={s.text} onChange={(e) => this.setSection(i, 'text', e.target.value)} />
                                                            </label>
                                                        </div>
                                                        <button type="button" className="se-row-del" aria-label="حذف القسم" title="حذف القسم" onClick={() => this.removeSection(i)}>
                                                            <i className="pi pi-trash"></i>
                                                        </button>
                                                    </div>
                                                ))}
                                                <button type="button" className="se-add" onClick={this.addSection}>
                                                    <i className="pi pi-plus"></i>إضافة قسم
                                                </button>
                                            </div>
                                        </section>

                                        <section className="ui-sec" id="br-approach" style={{ '--d': '300ms' }}>
                                            {head('br-approach', '٦', 'القسم الختامي', 'اختياري، بيقفل صفحة «عني» بجملة من عندك')}
                                            <div className="ui-fields">
                                                {this.text('approachTitle', 'العنوان', '')}
                                                {this.text('approachTag', 'جملة قصيرة', '')}
                                                {this.area('approachText', 'النص')}
                                            </div>
                                        </section>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </>
        );
    }
}

export default Branding;
